import React, { useState } from 'react';
import { Code2, X, CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = '';

/**
 * Compact integrations lead-gen CTA shown at the top of /api-reference.
 * There is no public sandbox today; this is a "talk to our integrations
 * team" banner that submits to POST /api/integrations-enquiries.
 */
const SandboxCredentialsCta = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [useCase, setUseCase] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !businessName.trim()) {
      toast.error('Email and business name are required.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/integrations-enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          business_name: businessName.trim(),
          use_case: useCase.trim() || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Request failed');
      }
      setSubmitted(true);
      toast.success('Request received — our team will be in touch.');
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setOpen(false);
    // keep submitted state so repeated clicks show the confirmation until they reload
  };

  return (
    <>
      {/* Inline banner / trigger */}
      <section
        className="relative overflow-hidden bg-gradient-to-r from-[#1F4E79] via-[#25598a] to-[#1F4E79] rounded-lg shadow-lg p-5 sm:p-6 mb-8"
        data-testid="sandbox-cta-banner"
      >
        <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <div className="inline-flex items-center justify-center w-11 h-11 bg-white/10 border border-white/20 rounded-lg flex-shrink-0">
            <Code2 size={22} className="text-white" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] font-bold text-white/80 uppercase tracking-[0.15em] mb-0.5">
              Ready to integrate?
            </p>
            <h3 className="text-base sm:text-lg font-bold text-white leading-tight">
              Talk to Our Integrations Team
            </h3>
            <p className="text-xs sm:text-sm text-white/70 mt-0.5">
              Tell us about your build — we'll help you map out your integration path.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#1F4E79] hover:bg-white/90 active:scale-[0.98] text-sm font-bold rounded-md shadow-md transition-all duration-150 flex-shrink-0"
            data-testid="sandbox-cta-open-btn"
          >
            {submitted ? (
              <>
                <CheckCircle2 size={16} />
                Request Submitted
              </>
            ) : (
              <>
                Get In Touch
                <ArrowRight size={14} strokeWidth={2.5} />
              </>
            )}
          </button>
        </div>
      </section>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={resetAndClose}
          data-testid="sandbox-modal-backdrop"
        >
          <div
            className="relative w-full max-w-md bg-white rounded-lg shadow-2xl p-6 sm:p-7 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="sandbox-modal"
          >
            <button
              type="button"
              onClick={resetAndClose}
              className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              aria-label="Close"
              data-testid="sandbox-modal-close"
            >
              <X size={18} />
            </button>

            {submitted ? (
              <div className="text-center py-4" data-testid="sandbox-modal-success">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-4">
                  <CheckCircle2 size={28} className="text-green-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Thanks — we've got it</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-5">
                  Our integrations team will reach out to <strong className="text-gray-900">{email}</strong> to discuss your build. In the meantime, browse the <em>Request Reference</em> samples in our docs to get familiar with the request and response structure.
                </p>
                <button
                  type="button"
                  onClick={resetAndClose}
                  className="px-5 py-2 bg-[#1F4E79] hover:bg-[#163a5f] text-white text-sm font-semibold rounded-md transition-colors"
                  data-testid="sandbox-modal-done"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <Code2 size={16} className="text-[#1F4E79]" />
                  <p className="text-[10px] font-bold text-[#1F4E79] uppercase tracking-[0.15em]">Integrations</p>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Start Your Integration</h3>
                <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                  Tell us about your integration requirements and our team will be in touch to discuss your use case, technical requirements and the next steps for connecting to the eCOMM Acquiring API.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="sandbox-form">
                  <div>
                    <label htmlFor="sb-email" className="block text-xs font-semibold text-gray-700 mb-1">
                      Work Email <span className="text-[#c32222]">*</span>
                    </label>
                    <input
                      id="sb-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder=""
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1F4E79]/30 focus:border-[#1F4E79] transition-colors"
                      data-testid="sandbox-input-email"
                    />
                  </div>

                  <div>
                    <label htmlFor="sb-business" className="block text-xs font-semibold text-gray-700 mb-1">
                      Business Name <span className="text-[#c32222]">*</span>
                    </label>
                    <input
                      id="sb-business"
                      type="text"
                      required
                      maxLength={200}
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      placeholder=""
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1F4E79]/30 focus:border-[#1F4E79] transition-colors"
                      data-testid="sandbox-input-business"
                    />
                  </div>

                  <div>
                    <label htmlFor="sb-use-case" className="block text-xs font-semibold text-gray-700 mb-1">
                      Use Case <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <textarea
                      id="sb-use-case"
                      rows={3}
                      maxLength={1000}
                      value={useCase}
                      onChange={(e) => setUseCase(e.target.value)}
                      placeholder=""
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1F4E79]/30 focus:border-[#1F4E79] transition-colors resize-none"
                      data-testid="sandbox-input-use-case"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1F4E79] hover:bg-[#163a5f] active:scale-[0.98] disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm font-bold rounded-md shadow-sm transition-all duration-150"
                    data-testid="sandbox-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Submitting…
                      </>
                    ) : (
                      <>Send Request</>
                    )}
                  </button>

                  <p className="text-[10px] text-gray-500 text-center leading-relaxed">
                    We'll only use your email to reply to your enquiry. No spam, no third parties.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SandboxCredentialsCta;
